import type { Monaco } from '@monaco-editor/react';

import { createRangeVariablesDependencyProposal } from '@finley-technologies/calc-agent';
import { FormulaEngine } from '@finley-technologies/finley-types';

import type { MonacoType } from '../../../../../../../../types/global.d';

export const defaultEditorOptions: MonacoType.editor.IEditorOptions = {
  quickSuggestions: {
    other: 'inline',
    comments: false,
    strings: true,
  },
  suggest: {
    snippetsPreventQuickSuggestions: false,
  },
  fixedOverflowWidgets: true,
  minimap: {
    enabled: false,
  },
};

export const defaultTheme = 'vs-dark';
export const lightTheme = 'clouds';

type VariableType = 'onlyRow' | 'onlyRange' | 'all';

/**
 * Adds range and kind properties for proposals generated by helpers from calc-agent
 *
 * @param range range where to insert text (position is calculated in `provideCompletionItems`)
 * @param kind defining what type of value autocomplete is providing (`monaco.languages.CompletionItemKind`)
 * @param insertTextRules used for suggestions that have multiple tabbable fields (`monaco languages.CompletionItemInsertTextRule`)
 */
const addMonacoEditorDynamicCompletionItemProperties = (
  proposals: FormulaEngine.FormulaDependencyType[],
  completionInputs: Pick<
    MonacoType.languages.CompletionItem,
    'range' | 'kind' | 'insertTextRules'
  >,
  options: {
    includedVariables: VariableType;
  },
): MonacoType.languages.CompletionItem[] => {
  const { kind, range, insertTextRules } = completionInputs;
  const { includedVariables } = options;

  const completionItems: MonacoType.languages.CompletionItem[] = [];

  const addItems = (items: FormulaEngine.FormulaDependencyType[]): void => {
    for (const item of items) {
      completionItems.push({
        ...item,
        range,
        kind,
        insertTextRules,
      });
    }
  };

  if (includedVariables === 'onlyRow' || includedVariables === 'all') {
    addItems(proposals);
  }

  if (includedVariables === 'onlyRange' || includedVariables === 'all') {
    const rangedProposals = createRangeVariablesDependencyProposal(proposals);
    addItems(rangedProposals);
  }

  return completionItems;
};

export const setupMonacoEditorHelpers = (monaco: Monaco) => {
  const addMonacoEditorVariables = (
    proposals: FormulaEngine.FormulaDependencyType[],
    completionInputs: Pick<
      MonacoType.languages.CompletionItem,
      'range' | 'insertTextRules'
    >,
    options?: {
      includedVariables?: VariableType;
    },
  ): MonacoType.languages.CompletionItem[] => {
    return addMonacoEditorDynamicCompletionItemProperties(
      proposals,
      {
        ...completionInputs,
        kind: monaco.languages.CompletionItemKind.Variable,
      },
      { includedVariables: options?.includedVariables ?? 'onlyRow' },
    );
  };

  const addMonacoEditorFunctions = (
    proposals: FormulaEngine.FormulaDependencyType[],
    completionInputs: Pick<
      MonacoType.languages.CompletionItem,
      'range' | 'kind' | 'insertTextRules'
    >,
  ): MonacoType.languages.CompletionItem[] => {
    return addMonacoEditorDynamicCompletionItemProperties(
      proposals,
      completionInputs,
      { includedVariables: 'onlyRow' },
    );
  };

  return { addMonacoEditorVariables, addMonacoEditorFunctions };
};
